<script>
import {
  resolveActionName,
  resolveTeamIds,
  resolveLabels,
  resolveAgents,
} from 'dashboard/routes/dashboard/settings/macros/macroHelper';

export default {
  name: 'MacroInfoModal',
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    macro: {
      type: Object,
      default: () => ({}),
    },
    teams: {
      type: Array,
      default: () => [],
    },
    agents: {
      type: Array,
      default: () => [],
    },
    labels: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close'],
  mounted() {
    document.addEventListener('keydown', this.handleEscKey);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleEscKey);
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    handleEscKey(event) {
      if (event.key === 'Escape' && this.show) {
        this.closeModal();
      }
    },
    getResolvedMacro(macro) {
      if (!macro || !macro.actions) return [];
      return macro.actions.map(action => {
        return {
          actionName: resolveActionName(action.action_name),
          actionValue: this.getActionValue(
            action.action_name,
            action.action_params
          ),
          actionIcon: this.getActionIcon(action.action_name),
          actionType: this.getActionType(action.action_name),
        };
      });
    },
    getActionIcon(actionKey) {
      const iconMap = {
        assign_team: 'i-lucide-users',
        assign_agent: 'i-lucide-user',
        add_label: 'i-lucide-tag',
        remove_label: 'i-lucide-x',
        remove_assigned_team: 'i-lucide-user-x',
        send_email_transcript: 'i-lucide-mail',
        mute_conversation: 'i-lucide-volume-x',
        snooze_conversation: 'i-lucide-clock',
        resolve_conversation: 'i-lucide-check-circle',
        send_attachment: 'i-lucide-paperclip',
        send_message: 'i-lucide-message-circle',
        add_private_note: 'i-lucide-sticky-note',
        change_priority: 'i-lucide-flag',
        send_webhook_event: 'i-lucide-globe',
      };
      return iconMap[actionKey] || 'i-lucide-settings';
    },
    getActionType(actionKey) {
      const destructiveActions = ['remove_label', 'remove_assigned_team', 'mute_conversation'];
      const creativeActions = ['assign_team', 'assign_agent', 'add_label', 'send_message', 'add_private_note', 'send_webhook_event', 'send_attachment', 'send_email_transcript'];
      
      if (destructiveActions.includes(actionKey)) {
        return 'destructive';
      } else if (creativeActions.includes(actionKey)) {
        return 'creative';
      }
      return 'neutral';
    },
    getActionColors(actionType) {
      const colorMap = {
        destructive: {
          iconBg: 'bg-n-ruby-2 dark:bg-n-ruby-2',
          iconColor: 'text-n-ruby-11 dark:text-n-ruby-10',
          cardBg: 'bg-n-ruby-2 dark:bg-n-ruby-4'
        },
        creative: {
          iconBg: 'bg-woot-50 dark:bg-woot-900',
          iconColor: 'text-woot-500 dark:text-woot-400',
          cardBg: 'bg-woot-25 dark:bg-woot-800/50'
        },
        neutral: {
          iconBg: 'bg-slate-100 dark:bg-n-slate-2',
          iconColor: 'text-slate-600 dark:text-slate-400',
          cardBg: 'bg-n-slate-2 dark:bg-n-slate-5'
        }
      };
      return colorMap[actionType] || colorMap.neutral;
    },
    getActionValue(key, params) {
      const actionsMap = {
        assign_team: resolveTeamIds(this.teams, params),
        add_label: resolveLabels(this.labels, params),
        remove_label: resolveLabels(this.labels, params),
        assign_agent: resolveAgents(this.agents, params),
        mute_conversation: null,
        snooze_conversation: null,
        resolve_conversation: null,
        remove_assigned_team: null,
        send_webhook_event: params[0],
        send_message: params[0],
        send_email_transcript: params[0],
        add_private_note: params[0],
      };
      return actionsMap[key] || '';
    },
  },
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[1000] flex items-center justify-center p-8 bg-n-slate-12/10 dark:bg-n-slate-1/10 backdrop-blur-sm"
    @click.self="closeModal"
  >
    <div class="w-full max-w-3xl max-h-[90vh] bg-n-solid-2 rounded-xl shadow-xl flex flex-col">
      <div class="flex items-center justify-between border-b border-n-weak dark:border-n-strong p-5 flex-shrink-0">
        <div class="flex items-center gap-4">
          <div class="w-12 h-12 rounded-full bg-n-brand/10 flex items-center justify-center">
            <span class="i-lucide-zap text-n-brand text-lg" />
          </div>
          <div class="flex flex-col gap-0.5">
            <span class="text-lg font-semibold text-n-slate-12 dark:text-n-slate-12">
              {{ macro.name || 'Macro' }}
            </span>
            <div class="flex items-center">
              <router-link
                :to="`/app/accounts/${$route.params.accountId}/settings/macros/${macro.id}/edit`"
                class="text-sm text-n-brand hover:underline"
                title="Editar macro"
              >
                Editar macro
              </router-link>
            </div>
          </div>
        </div>
        <button
          class="z-10 ltr:right-2 rtl:left-2 top-2 inline-flex items-center min-w-0 gap-2 transition-all duration-200 ease-in-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-slate-12 dark:text-n-slate-12 hover:enabled:bg-n-alpha-2 dark:hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 dark:focus-visible:bg-n-alpha-2 outline-transparent h-10 w-10 p-0 text-sm font-medium justify-center"
          @click="closeModal"
        >
          <span class="i-lucide-x flex-shrink-0" />
        </button>
      </div>

      <div class="p-5 overflow-y-auto flex-1">
        <div class="flex items-center justify-between mb-6">
          <h6 class="text-sm text-slate-600 dark:text-slate-400 font-medium">
            Ações que serão executadas:
          </h6>
          <span class="text-sm text-n-slate-11">
            {{ getResolvedMacro(macro).length }} {{ getResolvedMacro(macro).length === 1 ? 'ação' : 'ações' }}
          </span>
        </div>
        
        <div v-if="getResolvedMacro(macro).length > 0" class="grid grid-cols-12 gap-6">
          <!-- Left Column: Vertical Timeline -->
          <div class="col-span-1">
            <div class="relative">
              <!-- Timeline steps -->
              <div class="space-y-0 mt-5">
                <div
                  v-for="(action, i) in getResolvedMacro(macro)"
                  :key="i"
                  class="flex flex-col items-center"
                >
                  <!-- Circle -->
                  <div class="relative z-10 w-8 h-8 rounded-full border-2 border-primary-600 bg-n-solid-2 flex items-center justify-center">
                    <span class="text-xs font-semibold text-primary-600">{{ i + 1 }}</span>
                  </div>
                  
                  <!-- Line after circle (except for last item) -->
                  <div 
                    v-if="i < getResolvedMacro(macro).length - 1"
                    class="w-0.5 bg-primary-600 transition-all duration-500 ease-out"
                    style="height: 66px;"
                  ></div>
                </div>
              </div>
            </div>
          </div>
          
          <!-- Right Column: Action Blocks -->
          <div class="col-span-11">
            <div class="space-y-6">
              <div
                v-for="(action, i) in getResolvedMacro(macro)"
                :key="i"
                class="macro-block"
              >
                <div class="rounded-lg p-4 flex gap-3 transition-all duration-200 ease-in-out hover:translate-x-1 hover:brightness-95 dark:hover:brightness-110" :class="[getActionColors(action.actionType).cardBg, action.actionValue ? 'items-start' : 'items-center']">
                  <div class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" :class="getActionColors(action.actionType).iconBg">
                    <span :class="[action.actionIcon, getActionColors(action.actionType).iconColor]" class="text-lg" />
                  </div>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-n-slate-12 dark:text-n-slate-12" :class="{ 'mb-1': action.actionValue }">
                      {{ action.actionName }}
                    </p>
                    <p v-if="action.actionValue" class="text-sm text-n-slate-11 dark:text-n-slate-11">
                      {{ action.actionValue }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div v-else class="text-center py-8">
          <p class="text-n-slate-10 dark:text-n-slate-10">
            Esta macro não possui ações configuradas.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>